import { MosaicTrafficLightsValue } from "aic";
import { CommentFilterOptionValue } from "components/common/comments/interface";
import { TocItemUpdateMessage } from "contexts/signalr/interface";
import { TlfUpdateMessage } from "contexts/tlf/interface";
import { eShowTiFo, eTlfItemType } from "functions/api/enums";
import {
  isTitlesFootnotesReadDto,
  TitlesFootnotesReadDto,
} from "functions/api/tlfItem/TitlesFootnotesReadDto";
import { TocItemsUpdateProps } from "functions/api/toc/updateTocItems";
import {
  IMosaicTocExpanded,
  IMosaicTocItemUpdate,
  IRemovedItemProps,
  MosaicTocEntryDto,
  TocUpdateMessage,
} from "./interface";
import { MosaicTocSectionClass } from "./MosaicTocSectionClass";
import { tocSort } from "./tocSort";

export const ADD_SECTION = "ADD_SECTION";
export const CLEAR_REMOVED = "CLEAR_REMOVED";
export const DELETE = "DELETE";
export const FROM_SIGNALR = "FROM_SIGNALR";
export const ADD_NEW_TOC_ENTRIES = "ADD_NEW_TOC_ENTRIES";
export const MOVE_ITEM = "MOVE_ITEM";
export const REASSIGN_TOC_NUMBERS = "REASSIGN_TOC_NUMBERS";
export const REFRESH = "REFRESH";
export const SAVE_FAIL = "SAVE_FAIL";
export const SAVE_SUCCESS = "SAVE_SUCCESS";
export const SAVING_TOC_ITEM_VIA_TLF = "SAVING_TOC_ITEM_VIA_TLF";
export const SET_TYPE_FILTER = "SET_TYPE_FILTER";
export const SORT = "SORT";
export const UPDATE_AFTER_PATCH = "UPDATE_AFTER_PATCH";
export const UPDATE_COMMENTS_FILTER = "UPDATE_COMMENTS_FILTER";
export const UPDATE_ENTRY = "UPDATE_ENTRY";
export const UPDATE_LAST_BACKEDUP_AT = "UPDATE_LAST_BACKEDUP_AT";
export const UPDATE_TRAFFIC_LIGHTS_FILTER = "UPDATE_TRAFFIC_LIGHTS_FILTER";
export const UPDATE_READONLY = "UPDATE_READONLY";
export const UPDATE_SEARCH_TEXT = "UPDATE_SEARCH_TEXT";
export const UPDATE_SECTION = "UPDATE_SECTION";
export const UPDATE_VIEW_TIFO = "UPDATE_VIEW_TIFO";

export type TocOperation =
  | typeof ADD_SECTION
  | typeof CLEAR_REMOVED
  | typeof DELETE
  | typeof FROM_SIGNALR
  | typeof ADD_NEW_TOC_ENTRIES
  | typeof MOVE_ITEM
  | typeof REASSIGN_TOC_NUMBERS
  | typeof REFRESH
  | typeof SAVE_FAIL
  | typeof SAVE_SUCCESS
  | typeof SAVING_TOC_ITEM_VIA_TLF
  | typeof SET_TYPE_FILTER
  | typeof SORT
  | typeof UPDATE_AFTER_PATCH
  | typeof UPDATE_COMMENTS_FILTER
  | typeof UPDATE_ENTRY
  | typeof UPDATE_LAST_BACKEDUP_AT
  | typeof UPDATE_TRAFFIC_LIGHTS_FILTER
  | typeof UPDATE_READONLY
  | typeof UPDATE_SEARCH_TEXT
  | typeof UPDATE_SECTION
  | typeof UPDATE_VIEW_TIFO;

export interface ITocActionBase {
  operation: TocOperation;
}

export interface ITocActionNoParams extends ITocActionBase {
  operation:
    | typeof CLEAR_REMOVED
    | typeof REASSIGN_TOC_NUMBERS
    | typeof SAVE_FAIL
    | typeof SAVING_TOC_ITEM_VIA_TLF
    | typeof SORT
    | typeof UPDATE_AFTER_PATCH;
}

export interface ITocActionAddSection extends ITocActionBase {
  operation: typeof ADD_SECTION;
  newId: string;
}

export interface ITocActionDelete extends ITocActionBase {
  operation: typeof DELETE;
  itemId: string;
}

export interface ITocActionFromSignalR extends ITocActionBase {
  operation: typeof FROM_SIGNALR;
  items: TocItemUpdateMessage[];
  deletedItemId?: string;
  receivedMessage: TocUpdateMessage;
}

export interface ITlfActionFromSignalR extends ITocActionBase {
  operation: typeof FROM_SIGNALR;
  item: string;
  updatedItemId?: string;
  receivedMessage: TlfUpdateMessage;
}

export interface ITocActionLoadEntries extends ITocActionBase {
  operation: typeof ADD_NEW_TOC_ENTRIES;
  entries: MosaicTocEntryDto[];
  loadTimeStamp: number;
}

export interface ITocActionMoveItem extends ITocActionBase {
  operation: typeof MOVE_ITEM;
  itemId: string;
  newIndex: number;
}

export interface ITocActionRefresh extends ITocActionBase {
  operation: typeof REFRESH;
  hierarchyId: string;
  items: (MosaicTocEntryDto | MosaicTocSectionClass)[];
  removedItems?: IRemovedItemProps[];
}

export interface ITocActionSetTypeFilter extends ITocActionBase {
  operation: typeof SET_TYPE_FILTER;
  showTypes: eTlfItemType[];
}

export interface ITocActionUpdateCommentsFilter extends ITocActionBase {
  operation: typeof UPDATE_COMMENTS_FILTER;
  commentsFilter: CommentFilterOptionValue;
}

export interface ITocActionUpdateLastSavedAt extends ITocActionBase {
  operation: typeof UPDATE_LAST_BACKEDUP_AT;
  backupTimeStamp: Date;
}

export interface ITocActionUpdateTrafficLightsFilter extends ITocActionBase {
  operation: typeof UPDATE_TRAFFIC_LIGHTS_FILTER;
  trafficLightFilter?: MosaicTrafficLightsValue;
}

export interface ITocActionUpdateEntry extends ITocActionBase {
  operation: typeof UPDATE_ENTRY;
  update: TitlesFootnotesReadDto;
}

export interface ITocActionUpdateReadonly extends ITocActionBase {
  operation: typeof UPDATE_READONLY;
  readOnly: boolean;
}

export interface ITocActionUpdateSection extends ITocActionBase {
  operation: typeof UPDATE_SECTION;
  update: IMosaicTocItemUpdate;
}

export interface ITocActionSaveSuccess extends ITocActionBase {
  operation: typeof SAVE_SUCCESS;
  itemsUpdate?: TocItemsUpdateProps;
  saveTimeStamp: number;
}

export interface ITocActionUpdateSearchText extends ITocActionBase {
  operation: typeof UPDATE_SEARCH_TEXT;
  searchText: string;
}

export interface ITocActionUpdateViewTiFo extends ITocActionBase {
  operation: typeof UPDATE_VIEW_TIFO;
  showTiFo: eShowTiFo;
}

export type ITocAction =
  | ITocActionNoParams
  | ITocActionAddSection
  | ITocActionDelete
  | ITocActionFromSignalR
  | ITocActionLoadEntries
  | ITocActionMoveItem
  | ITocActionRefresh
  | ITocActionSaveSuccess
  | ITocActionSetTypeFilter
  | ITocActionUpdateEntry
  | ITocActionUpdateCommentsFilter
  | ITocActionUpdateTrafficLightsFilter
  | ITocActionUpdateLastSavedAt
  | ITocActionUpdateReadonly
  | ITocActionUpdateSearchText
  | ITocActionUpdateSection
  | ITocActionUpdateViewTiFo;

/**
 * Reducer for ToC context state transitions.
 *
 * @param state - The current `IMosaicTocExpanded` state.
 * @param action - The `ITocAction` describing the update to perform.
 * @returns The updated `IMosaicTocExpanded` after applying the action.
 */
export const tocContextReducer = (
  state: IMosaicTocExpanded,
  action: ITocAction,
): IMosaicTocExpanded => {
  const newState: IMosaicTocExpanded = { ...state };

  switch (action.operation) {
    case ADD_SECTION: {
      /* istanbul ignore else */
      if (newState.tocItems.findIndex((i) => i.id === action.newId) === -1) {
        const newSection = new MosaicTocSectionClass({
          id: action.newId,
          primaryHierarchyId: newState.hierarchyId,
          sectionLabel: "",
          sectionNumber: "",
          order:
            newState.tocItems
              .map(
                (i) =>
                  i.tocOrder ??
                  // istanbul ignore next
                  0,
              )
              .reduce((a, b) => Math.min(a, b), 0) - 1,
          changed: true,
        });
        newState.tocItems.splice(0, 0, newSection);
      }
      newState.saving = true;
      break;
    }
    case CLEAR_REMOVED: {
      newState.removedItems = [];
      newState.changed = false;
      break;
    }
    case DELETE: {
      const removed = newState.tocItems.find((i) => i.id === action.itemId);
      /* istanbul ignore else */
      if (removed) {
        newState.tocItems = newState.tocItems.filter((i) => i.id !== action.itemId);
        if (removed.type === "Section") {
          newState.removedItems.push({ id: removed.id, changed: true });
        } else {
          newState.removedItems.push({
            id: removed.id,
            azsolId: removed.azsolId,
            version: removed.version,
            changed: true,
          });
        }
        newState.saving = true;
      }
      break;
    }
    case FROM_SIGNALR:
      // Delete item if found
      if (newState.tocItems.findIndex((i) => i.id === action.deletedItemId) > -1) {
        const ix = newState.tocItems.findIndex((i) => i.id === action.deletedItemId);
        newState.removedItems.push(...newState.tocItems.splice(ix, 1));
      }
      action.items.forEach((item) => {
        const ix = newState.tocItems.findIndex((i) => i.id === item.id);
        // Update item when found
        if (ix > -1 && item.type === eTlfItemType.section) {
          const current = newState.tocItems[ix] as MosaicTocSectionClass;
          current.update({
            ...item,
            saved: true,
          });
        } else if (ix > -1) {
          newState.tocItems[ix] = {
            ...(newState.tocItems[ix] as MosaicTocEntryDto),
            ...item,
            saved: true,
          };
        }
        // Add new item
        else if (item.type === eTlfItemType.section) {
          const newSection = new MosaicTocSectionClass({
            id: item.id,
            sectionNumber:
              item.tocNumber ??
              // istanbul ignore next
              "",
            sectionLabel:
              item.tocLabel ??
              // istanbul ignore next
              "",
            order:
              item.tocOrder ??
              // istanbul ignore next
              0,
            primaryHierarchyId: newState.hierarchyId,
            changed: false,
          });
          newState.tocItems.push(newSection);
        } else {
          newState.tocItems.push(item);
        }
      });
      newState.receivedMessages.push(action.receivedMessage);
      newState.tocItems.sort(
        (a, b) =>
          (a.tocOrder ??
            // istanbul ignore next
            0) -
          (b.tocOrder ??
            // istanbul ignore next
            0),
      );
      newState.saving = false;
      break;
    case ADD_NEW_TOC_ENTRIES: {
      const existingIds = newState.tocItems.map((i) => i.id);
      newState.tocItems = [
        ...newState.tocItems,
        ...action.entries.filter((i) => !existingIds.includes(i.id)),
      ].sort(
        (a, b) =>
          (a.tocOrder ??
            // istanbul ignore next
            0) -
          (b.tocOrder ??
            // istanbul ignore next
            0),
      );
      newState.saving = false;
      newState.lastMinorVersionSavedAt = action.loadTimeStamp;
      newState.sendUpdate = true;
      break;
    }
    case MOVE_ITEM: {
      const moveIndex = newState.tocItems.map((i) => i.id).indexOf(action.itemId);
      /* istanbul ignore else */
      if (moveIndex !== action.newIndex) {
        // Move item in the array
        newState.tocItems.splice(action.newIndex, 0, newState.tocItems.splice(moveIndex, 1)[0]);
        // Update tocOrder for items between the old and new index
        newState.tocItems.forEach((n, i) => {
          if (n.tocOrder !== i) {
            if (n instanceof MosaicTocSectionClass) {
              n.update({ id: n.id, saved: false, tocOrder: i });
            } else {
              n.tocOrder = i;
              n.changed = true;
              n.inError = false;
            }
          }
        });
        // Save any moved items
      }
      newState.saving = true;
      break;
    }
    case REASSIGN_TOC_NUMBERS: {
      newState.tocItems = newState.tocItems.sort(
        (a, b) =>
          (a.tocOrder ??
            // istanbul ignore next
            0) -
          (b.tocOrder ??
            // istanbul ignore next
            0),
      );
      let lastSection = "";
      let lastN = 0;
      const reassignedTocItems = newState.tocItems.map((n) => {
        if (n instanceof MosaicTocSectionClass) {
          lastSection = n.tocNumber;
          lastN = 0;
          return n; // No changes needed for sections
        } else {
          lastN++;
          const newTocNumber = lastSection !== "" ? lastSection + "." + lastN : "";
          if (newTocNumber !== n.tocNumber) {
            return {
              ...n,
              tocNumber: newTocNumber,
              changed: true,
              inError: false,
            };
          }
          return n;
        }
      });
      newState.tocItems = reassignedTocItems;
      newState.saving = true;
      break;
    }
    case REFRESH: {
      newState.changed = false;
      newState.hierarchyId = action.hierarchyId;
      newState.removedItems = action.removedItems ?? [];
      newState.saving = false;
      newState.saveError = false;
      newState.sendUpdate = false;
      newState.tocItems = action.items;
      newState.tocItems.sort(
        (a, b) =>
          (a.tocOrder ??
            // istanbul ignore next
            0) -
          (b.tocOrder ??
            // istanbul ignore next
            0),
      );
      break;
    }
    case SAVE_FAIL:
      newState.blocked = false;
      newState.changed = true;
      newState.saveError = true;
      newState.saving = false;
      newState.sendUpdate = false;
      newState.tocItems
        .filter((n) => n.changed)
        .forEach((n) => {
          if (n instanceof MosaicTocSectionClass) {
            n.markSaveFailed();
          } else {
            n.inError = true;
            n.changed = true;
          }
        });
      break;
    case SAVE_SUCCESS:
      newState.tocItems.forEach((n) => {
        if (n instanceof MosaicTocSectionClass) {
          n.markSaved();
        } else {
          n.inError = false;
          n.changed = false;
        }
      });
      newState.saving = false;
      newState.blocked = false;
      newState.saveError = false;
      newState.changed = false;
      newState.sendUpdate = true;
      newState.lastMinorVersionSavedAt = action.saveTimeStamp;
      break;
    case SAVING_TOC_ITEM_VIA_TLF:
      newState.blocked = "patching";
      break;
    case SORT: {
      newState.tocItems = newState.tocItems.sort((a, b) => tocSort(a.tocNumber, b.tocNumber));
      newState.tocItems.forEach((n, i) => {
        if (n.tocOrder !== i) {
          if (n instanceof MosaicTocSectionClass) {
            n.update({ id: n.id, saved: false, tocOrder: i });
          } else {
            n.tocOrder = i;
            n.changed = true;
            n.inError = false;
          }
        }
      });
      newState.saving = true;
      break;
    }
    case SET_TYPE_FILTER:
      newState.showTypes = action.showTypes;
      break;
    case UPDATE_AFTER_PATCH:
      newState.blocked = false;
      break;
    case UPDATE_COMMENTS_FILTER:
      newState.commentsFilter = action.commentsFilter;
      break;
    case UPDATE_ENTRY: {
      const ix = newState.tocItems.findIndex(
        (i) => i.id === action.update.id && isTitlesFootnotesReadDto(i),
      );
      /* istanbul ignore else */
      if (ix > -1) {
        const current = {
          ...newState.tocItems[ix],
        } as MosaicTocEntryDto;
        current.inError = false;
        // Updates will be saved by the entries TLF context
        current.changed = false;
        // Check if any of the properties in the update are different from the current
        Object.keys(action.update)
          .filter((key) => !["id", "saved", "changed", "inError"].includes(key))
          .forEach((key) => {
            if (action.update[key] !== current[key]) current[key] = action.update[key];
          });
        newState.tocItems = [
          ...newState.tocItems.slice(0, ix),
          current,
          ...newState.tocItems.slice(ix + 1),
        ];
      }
      newState.blocked = "getNewVersion";
      break;
    }
    case UPDATE_LAST_BACKEDUP_AT:
      newState.lastMajorVersionSavedAt = action.backupTimeStamp.getTime();
      break;
    case UPDATE_READONLY:
      newState.readOnly = action.readOnly;
      break;
    case UPDATE_SEARCH_TEXT:
      newState.searchText = action.searchText;
      break;
    case UPDATE_SECTION: {
      const ix = newState.tocItems.findIndex(
        (i) => i.id === action.update.id && i instanceof MosaicTocSectionClass,
      );
      /* istanbul ignore else */
      if (ix > -1) {
        const current = newState.tocItems[ix] as MosaicTocSectionClass;
        current.update(action.update);
        if (current.changed) {
          newState.saving = true;
        }
      }
      break;
    }
    case UPDATE_TRAFFIC_LIGHTS_FILTER:
      newState.trafficLightsFilter = action.trafficLightFilter;
      break;
    case UPDATE_VIEW_TIFO:
      newState.showTiFo = action.showTiFo;
      break;
  }

  return newState;
};
